import { DOCUMENT, NgTemplateOutlet, getLocaleFirstDayOfWeek } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  LOCALE_ID,
  numberAttribute,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TimeListComponent } from '../../timepicker/time-list/time-list.component';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { calendarIcon, chevronLeftIcon, chevronRightIcon, clockIcon, xIcon } from '@progress/kendo-svg-icons';

import {
  autoFillDateTime,
  clampDate,
  createMonthCalendar,
  CueDateTimePart,
  CueDateTimePickerCalendarCell,
  datePartAtCaret,
  formatDateTime,
  formatPlaceholder,
  getIsoWeekNumber,
  getStep,
  incrementDatePart,
  isDateDisabled,
  isValidDate,
  parseDateTime,
  range,
  resolveDateTimeFormat,
  sameCalendarDay,
  sameDate,
} from './cue-datetimepicker.utils';
import {
  CueDateTimePickerActiveTab,
  CueDateTimePickerAdaptiveMode,
  CueDateTimePickerCalendarType,
  CueDateTimePickerCalendarView,
  CueDateTimePickerCellContext,
  CueDateTimePickerDisabledDates,
  CueDateTimePickerFillMode,
  CueDateTimePickerFormatPlaceholder,
  CueDateTimePickerFormatSettings,
  CueDateTimePickerIncrementalSteps,
  CueDateTimePickerMessages,
  CueDateTimePickerPopupSettings,
  CueDateTimePickerPreventableEvent,
  CueDateTimePickerRounded,
  CueDateTimePickerSize,
  CueDateTimePickerWeekDaysFormat,
  CueDateTimePickerWeekNumberContext,
} from './cue-datetimepicker.types';

const DEFAULT_MESSAGES: CueDateTimePickerMessages = {
  accept: 'Set',
  cancel: 'Cancel',
  clear: 'Clear date and time',
  dateTab: 'Date',
  hour: 'Hour',
  minute: 'Minute',
  nextPeriod: 'Next period',
  open: 'Choose date and time',
  previousPeriod: 'Previous period',
  second: 'Second',
  timeTab: 'Time',
  today: 'Today',
};

const ESSENTIAL_INPUT_ATTRIBUTES = new Set([
  'aria-controls',
  'aria-expanded',
  'aria-haspopup',
  'disabled',
  'readonly',
  'role',
  'tabindex',
  'type',
  'value',
]);

let nextDateTimePickerId = 0;

@Component({
  selector: 'cue-datetimepicker',
  templateUrl: './cue-datetimepicker.component.html',
  styleUrl: './cue-datetimepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, CdkTrapFocus, SVGIconComponent, NgTemplateOutlet, TimeListComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CueDatetimepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CueDatetimepickerComponent),
      multi: true,
    },
  ],
  host: {
    '[class.cue-datetimepicker--disabled]': 'isDisabled()',
    '[class.cue-datetimepicker--active]': 'isActive',
    '[class.cue-datetimepicker--time-only]': 'timeOnly()',
    '[class.cue-datetimepicker--small]': 'size() === "small"',
    '[class.cue-datetimepicker--large]': 'size() === "large"',
    '[class.cue-datetimepicker--flat]': 'fillMode() === "flat"',
    '[class.cue-datetimepicker--outline]': 'fillMode() === "outline"',
    '[class.cue-datetimepicker--caret-hidden]': '!allowCaretMode()',
    '[class.cue-datetimepicker--rounded-small]': 'rounded() === "small"',
    '[class.cue-datetimepicker--rounded-large]': 'rounded() === "large"',
    '[class.cue-datetimepicker--rounded-full]': 'rounded() === "full"',
    '[class.cue-datetimepicker--rounded-none]': 'rounded() === "none"',
  },
})
export class CueDatetimepickerComponent implements ControlValueAccessor, Validator {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly defaultLocale = inject(LOCALE_ID);

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('dateTimeInput');
  readonly popupElement = viewChild<ElementRef<HTMLElement>>('popup');

  readonly adaptiveMode = input<CueDateTimePickerAdaptiveMode>('none');
  readonly adaptiveSubtitle = input('');
  readonly adaptiveTitle = input('');
  readonly allowCaretMode = input(false, { transform: booleanAttribute });
  readonly animateCalendarNavigation = input(false, { transform: booleanAttribute });
  readonly autoCorrectParts = input(true, { transform: booleanAttribute });
  readonly autoFill = input(false, { transform: booleanAttribute });
  readonly autoSwitchKeys = input<string[]>([]);
  readonly autoSwitchParts = input(true, { transform: booleanAttribute });
  readonly calendarType = input<CueDateTimePickerCalendarType>('infinite');
  readonly cancelButton = input(true, { transform: booleanAttribute });
  readonly clearButton = input(false, { transform: booleanAttribute });
  readonly defaultTab = input<CueDateTimePickerActiveTab>('date');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly disabledDates = input<CueDateTimePickerDisabledDates>([]);
  readonly disabledDatesValidation = input(true, { transform: booleanAttribute });
  readonly enableMouseWheel = input(true, { transform: booleanAttribute });
  readonly enterKeyBehavior = input<'open' | 'blur'>('open');
  readonly fillMode = input<CueDateTimePickerFillMode>('solid');
  readonly focusedDate = input<Date>();
  readonly footer = input(false, { transform: booleanAttribute });
  readonly format = input<string | CueDateTimePickerFormatSettings>('M/d/yyyy HH:mm');
  readonly formatPlaceholder = input<CueDateTimePickerFormatPlaceholder>('formatPattern');
  readonly incompleteDateValidation = input(false, { transform: booleanAttribute });
  readonly inputAttributes = input<Record<string, string>>({});
  readonly locale = input(this.defaultLocale);
  readonly max = input<Date>();
  readonly messages = input<Partial<CueDateTimePickerMessages>>({});
  readonly min = input<Date>();
  readonly placeholder = input<string>();
  readonly popupSettings = input<CueDateTimePickerPopupSettings>({});
  readonly popupOrigin = input<ElementRef<HTMLElement>>();
  readonly rangeValidation = input(true, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly readOnlyInput = input(false, { transform: booleanAttribute });
  readonly rounded = input<CueDateTimePickerRounded>('medium');
  readonly showOtherMonthDays = input(true, { transform: booleanAttribute });
  readonly size = input<CueDateTimePickerSize>('medium');
  readonly steps = input<CueDateTimePickerIncrementalSteps>();
  readonly tabindex = input(0, { transform: numberAttribute });
  readonly timeOnly = input(false, { transform: booleanAttribute });
  readonly twoDigitYearMax = input(68, { transform: numberAttribute });
  readonly value = input<Date | null>();
  readonly weekDaysFormat = input<CueDateTimePickerWeekDaysFormat>('short');
  readonly weekNumber = input(false, { transform: booleanAttribute });

  readonly cellTemplate = input<TemplateRef<CueDateTimePickerCellContext>>();
  readonly centuryCellTemplate = input<TemplateRef<CueDateTimePickerCellContext>>();
  readonly decadeCellTemplate = input<TemplateRef<CueDateTimePickerCellContext>>();
  readonly footerTemplate = input<TemplateRef<unknown>>();
  readonly headerTemplate = input<TemplateRef<unknown>>();
  readonly headerTitleTemplate = input<TemplateRef<{ $implicit: string; title: string }>>();
  readonly monthCellTemplate = input<TemplateRef<CueDateTimePickerCellContext>>();
  readonly weekNumberTemplate = input<TemplateRef<CueDateTimePickerWeekNumberContext>>();
  readonly yearCellTemplate = input<TemplateRef<CueDateTimePickerCellContext>>();

  readonly valueChange = output<Date | null>();
  readonly open = output<CueDateTimePickerPreventableEvent>();
  readonly close = output<CueDateTimePickerPreventableEvent>();
  // The aliases preserve the Kendo-compatible event names while focus() and blur() remain public methods.
  // eslint-disable-next-line @angular-eslint/no-output-rename
  readonly focused = output<FocusEvent>({ alias: 'focus' });
  // eslint-disable-next-line @angular-eslint/no-output-rename
  readonly blurred = output<FocusEvent>({ alias: 'blur' });

  readonly calendarIcon = calendarIcon;
  readonly chevronLeftIcon = chevronLeftIcon;
  readonly chevronRightIcon = chevronRightIcon;
  readonly clockIcon = clockIcon;
  readonly xIcon = xIcon;
  readonly popupId = `cue-datetimepicker-popup-${nextDateTimePickerId++}`;

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  private readonly openState = signal(false);
  private readonly activeState = signal(false);
  private readonly formDisabled = signal(false);
  private readonly inputFocused = signal(false);
  private readonly currentValue = signal<Date | null>(null);
  private readonly inputBaseValue = signal(new Date(Math.floor(Date.now() / 60_000) * 60_000));
  private readonly pendingValue = signal<Date | null>(null);
  private readonly inputIncomplete = signal(false);
  private readonly runtimePopupSettings = signal<CueDateTimePickerPopupSettings>({});
  private readonly appliedInputAttributes = new Set<string>();

  readonly activeTab = signal<CueDateTimePickerActiveTab>('date');
  readonly calendarView = signal<CueDateTimePickerCalendarView>('month');
  readonly displayedMonth = signal(new Date());
  readonly inputText = signal('');
  readonly calendarNavigating = signal(false);

  readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  readonly effectiveMessages = computed(() => ({ ...DEFAULT_MESSAGES, ...this.messages() }));
  readonly effectivePopupSettings = computed(() => ({
    ...this.popupSettings(),
    ...this.runtimePopupSettings(),
  }));
  readonly popupPanelClasses = computed(() => this.normalizePopupClasses(this.effectivePopupSettings().popupClass));
  readonly popupHasAnimation = computed(() => this.effectivePopupSettings().animate !== false);
  readonly isAdaptive = computed(() => this.adaptiveMode() === 'auto');
  readonly resolvedPlaceholder = computed(() =>
    this.placeholder() === undefined ? formatPlaceholder(this.format(), this.formatPlaceholder()) : this.placeholder()!
  );
  readonly firstDayOfWeek = computed(() => {
    try {
      return getLocaleFirstDayOfWeek(this.locale());
    } catch {
      return 1;
    }
  });
  readonly weekdayLabels = computed(() => {
    const width = this.weekDaysFormat() === 'narrow' ? 'narrow' : 'short';
    const formatter = new Intl.DateTimeFormat(this.locale(), { weekday: width });
    const firstSunday = new Date(2024, 0, 7);
    return range(7).map((offset) => {
      const date = new Date(firstSunday);
      date.setDate(firstSunday.getDate() + ((this.firstDayOfWeek() + offset) % 7));
      return formatter.format(date);
    });
  });
  readonly calendarRows = computed(() =>
    createMonthCalendar(
      this.displayedMonth(),
      this.firstDayOfWeek(),
      this.pendingValue(),
      this.min(),
      this.max(),
      this.disabledDates(),
      this.showOtherMonthDays()
    )
  );
  readonly periodCells = computed(() => this.createPeriodCells());
  readonly calendarHeading = computed(() => this.getCalendarHeading());
  readonly hourOptions = computed(() => {
    const useTwelveHours = this.usesTwelveHourClock();
    const options = useTwelveHours
      ? range(13, getStep(this.steps(), 'hour'), 1)
      : range(24, getStep(this.steps(), 'hour'));
    return this.includeSelectedOption(options, this.selectedHour());
  });
  readonly minuteOptions = computed(() =>
    this.includeSelectedOption(range(60, getStep(this.steps(), 'minute')), this.selectedMinute())
  );
  readonly secondOptions = computed(() =>
    this.includeSelectedOption(range(60, getStep(this.steps(), 'second')), this.selectedSecond())
  );
  readonly showSeconds = computed(
    () => /s/.test(resolveDateTimeFormat(this.format(), true)) || (this.steps()?.second ?? 0) > 0
  );
  readonly usesTwelveHourClock = computed(() => /h/.test(resolveDateTimeFormat(this.format(), true)));
  readonly selectedHour = computed(() => {
    const hour = this.pendingValue()?.getHours() ?? 0;
    if (!this.usesTwelveHourClock()) {
      return hour;
    }
    return hour % 12 || 12;
  });
  readonly selectedMinute = computed(() => this.pendingValue()?.getMinutes() ?? 0);
  readonly pendingTimeLabel = computed(() => formatDateTime(this.pendingValue(), this.format(), this.locale(), true));
  readonly selectedSecond = computed(() => this.pendingValue()?.getSeconds() ?? 0);
  readonly selectedPeriod = computed<'am' | 'pm'>(() => ((this.pendingValue()?.getHours() ?? 0) >= 12 ? 'pm' : 'am'));
  readonly timeColumns = computed(() => {
    const parts = [
      {
        part: 'hour' as const,
        label: this.effectiveMessages().hour,
        values: this.hourOptions(),
        selected: this.selectedHour(),
      },
      {
        part: 'minute' as const,
        label: this.effectiveMessages().minute,
        values: this.minuteOptions(),
        selected: this.selectedMinute(),
      },
      ...(this.showSeconds()
        ? [
            {
              part: 'second' as const,
              label: this.effectiveMessages().second,
              values: this.secondOptions(),
              selected: this.selectedSecond(),
            },
          ]
        : []),
    ];
    const columns = parts.map(({ part, label, values, selected }) => ({
      part: part as 'hour' | 'minute' | 'second' | 'period',
      label,
      selected: String(selected),
      options: values.map((value) => ({
        value: String(value),
        label: this.formatTimePart(value),
        disabled: this.isTimeOptionDisabled(part, value),
      })),
    }));
    if (this.usesTwelveHourClock()) {
      columns.push({
        part: 'period',
        label: 'AM/PM',
        selected: this.selectedPeriod(),
        options: ['am', 'pm'].map((value) => ({ value, label: value.toUpperCase(), disabled: false })),
      });
    }
    return columns;
  });
  readonly pendingAcceptDisabled = computed(() => {
    const value = this.pendingValue();
    return !!value && (this.isOutsideRange(value) || isDateDisabled(value, this.disabledDates()));
  });
  readonly inputIsIncomplete = this.inputIncomplete.asReadonly();

  get isOpen(): boolean {
    return this.openState();
  }

  get isActive(): boolean {
    return this.activeState() || this.openState();
  }

  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  private readonly externalValueEffect = effect(() => {
    const value = this.value();
    if (value !== undefined) {
      this.applyExternalValue(isValidDate(value) ? value : null);
    }
  });

  private readonly displayFormatEffect = effect(() => {
    this.format();
    this.locale();
    if (!this.inputFocused() && !this.inputIncomplete()) {
      this.inputText.set(formatDateTime(this.currentValue(), this.format(), this.locale(), false));
    }
  });

  private readonly inputAttributesEffect = effect(() => {
    const element = this.inputElement()?.nativeElement;
    const attributes = this.inputAttributes();
    if (!element) {
      return;
    }

    for (const attribute of this.appliedInputAttributes) {
      if (!(attribute in attributes)) {
        element.removeAttribute(attribute);
        this.appliedInputAttributes.delete(attribute);
      }
    }

    for (const [attribute, value] of Object.entries(attributes)) {
      const normalizedAttribute = attribute.toLowerCase();
      if (ESSENTIAL_INPUT_ATTRIBUTES.has(normalizedAttribute)) {
        continue;
      }
      element.setAttribute(attribute, value);
      this.appliedInputAttributes.add(attribute);
    }
  });

  private readonly validationInputsEffect = effect(() => {
    this.min();
    this.max();
    this.rangeValidation();
    this.disabledDates();
    this.disabledDatesValidation();
    this.incompleteDateValidation();
    this.inputIncomplete();
    this.timeOnly();
    this.onValidatorChange();
  });

  focus(): void {
    if (this.openState()) {
      const popup = this.popupElement()?.nativeElement;
      if (popup) {
        popup.focus();
      } else {
        queueMicrotask(() => this.popupElement()?.nativeElement.focus());
      }
      return;
    }
    this.inputElement()?.nativeElement.focus();
  }

  blur(): void {
    const activeElement = this.document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }

  toggle(show = !this.openState()): void {
    if (show) {
      this.preparePopup();
    }
    this.openState.set(show && !this.isDisabled() && !this.readonly());
  }

  configurePopup(settings: CueDateTimePickerPopupSettings): void {
    this.runtimePopupSettings.set(settings);
  }

  handleInputFocus(event: FocusEvent): void {
    this.inputFocused.set(true);
    this.activeState.set(true);
    if (!this.inputIncomplete()) {
      this.inputText.set(formatDateTime(this.currentValue(), this.format(), this.locale(), true));
    }
    if (!this.allowCaretMode()) {
      queueMicrotask(() => this.selectDatePartAtCaret(event.target as HTMLInputElement));
    }
    this.focused.emit(event);
  }

  handleInputBlur(event: FocusEvent): void {
    setTimeout(() => {
      const activeElement = this.document.activeElement;
      const remainsActive =
        (activeElement && this.hostElement.nativeElement.contains(activeElement)) ||
        (activeElement && this.popupElement()?.nativeElement.contains(activeElement));
      if (!remainsActive) {
        if (this.inputIncomplete() && this.autoFill()) {
          const fallback = this.currentValue() ?? this.inputBaseValue();
          const parsed = autoFillDateTime(
            this.inputText(),
            this.format(),
            fallback,
            this.twoDigitYearMax(),
            this.autoCorrectParts(),
            this.locale()
          );
          if (parsed) {
            this.commitValue(parsed);
          }
        }
        this.inputFocused.set(false);
        this.activeState.set(false);
        if (!this.inputIncomplete()) {
          this.inputText.set(formatDateTime(this.currentValue(), this.format(), this.locale(), false));
        }
        this.onTouched();
        this.blurred.emit(event);
      }
    });
  }

  handleInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const text = inputElement.value;
    this.inputText.set(text);

    if (!text.trim()) {
      this.inputIncomplete.set(false);
      this.commitValue(null);
      return;
    }

    const parsed = parseDateTime(
      text,
      this.format(),
      this.currentValue() ?? this.inputBaseValue(),
      this.twoDigitYearMax(),
      this.autoCorrectParts(),
      this.locale()
    );
    if (parsed) {
      this.commitValue(parsed, false);
    } else {
      this.commitValue(null, false);
      this.inputIncomplete.set(true);
      if (this.autoSwitchParts()) {
        this.autoSwitchToNextPart(inputElement);
      }
    }
  }

  handleInputKeydown(event: KeyboardEvent): void {
    if (this.autoSwitchKeys().includes(event.key)) {
      event.preventDefault();
      this.moveCaretToNextPart(event.target as HTMLInputElement);
      return;
    }
    if (event.altKey && event.key === 'ArrowDown') {
      event.preventDefault();
      this.requestOpen();
      return;
    }
    if (event.altKey && event.key === 'ArrowUp') {
      event.preventDefault();
      this.requestClose();
      return;
    }
    if (event.key === 'Escape' && this.openState()) {
      event.preventDefault();
      this.requestClose();
      return;
    }
    if (event.key === 'Enter') {
      if (this.enterKeyBehavior() === 'blur') {
        event.preventDefault();
        this.blur();
        return;
      }
      if (this.inputIncomplete()) {
        this.handleInput(event);
      } else if (!this.openState()) {
        this.requestOpen();
      }
      return;
    }
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    event.preventDefault();
    const inputElement = event.target as HTMLInputElement;
    const part = datePartAtCaret(resolveDateTimeFormat(this.format(), true), inputElement.selectionStart ?? 0);
    this.incrementInputValue(part, event.key === 'ArrowUp' ? 1 : -1);
  }

  handleInputWheel(event: WheelEvent): void {
    if (!this.enableMouseWheel() || this.isDisabled() || this.readonly() || this.readOnlyInput()) {
      return;
    }
    event.preventDefault();
    const inputElement = event.target as HTMLInputElement;
    const part = datePartAtCaret(resolveDateTimeFormat(this.format(), true), inputElement.selectionStart ?? 0);
    this.incrementInputValue(part, event.deltaY < 0 ? 1 : -1);
  }

  requestToggle(): void {
    this.openState() ? this.requestClose() : this.requestOpen();
  }

  requestOpen(): void {
    if (this.openState() || this.isDisabled() || this.readonly()) {
      return;
    }
    const event = new CueDateTimePickerPreventableEvent();
    this.open.emit(event);
    if (event.isDefaultPrevented()) {
      return;
    }
    this.preparePopup();
    this.openState.set(true);
  }

  requestClose(): void {
    if (!this.openState()) {
      return;
    }
    const event = new CueDateTimePickerPreventableEvent();
    this.close.emit(event);
    if (!event.isDefaultPrevented()) {
      this.openState.set(false);
      this.calendarView.set('month');
      this.onTouched();
    }
  }

  handlePopupKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || (event.altKey && event.key === 'ArrowUp')) {
      event.preventDefault();
      this.requestClose();
      this.inputElement()?.nativeElement.focus();
    }
  }

  changeTab(tab: CueDateTimePickerActiveTab): void {
    this.activeTab.set(tab);
  }

  showHigherCalendarView(): void {
    const currentView = this.calendarView();
    if (currentView === 'month') this.calendarView.set('year');
    else if (currentView === 'year') this.calendarView.set('decade');
    else if (currentView === 'decade') this.calendarView.set('century');
  }

  navigateCalendar(direction: -1 | 1): void {
    const date = new Date(this.displayedMonth());
    const view = this.calendarView();
    if (view === 'month') date.setMonth(date.getMonth() + direction);
    if (view === 'year') date.setFullYear(date.getFullYear() + direction);
    if (view === 'decade') date.setFullYear(date.getFullYear() + direction * 10);
    if (view === 'century') date.setFullYear(date.getFullYear() + direction * 100);
    this.displayedMonth.set(date);
    if (this.animateCalendarNavigation()) {
      this.calendarNavigating.set(false);
      requestAnimationFrame(() => this.calendarNavigating.set(true));
      setTimeout(() => this.calendarNavigating.set(false), 160);
    }
  }

  handleCalendarWheel(event: WheelEvent): void {
    if (this.calendarType() !== 'infinite') {
      return;
    }
    event.preventDefault();
    this.navigateCalendar(event.deltaY < 0 ? -1 : 1);
  }

  selectCalendarCell(cell: CueDateTimePickerCalendarCell): void {
    if (cell.disabled || (!cell.label && this.calendarView() === 'month')) {
      return;
    }

    const view = this.calendarView();
    if (view === 'century') {
      this.displayedMonth.set(new Date(cell.date));
      this.calendarView.set('decade');
      return;
    }
    if (view === 'decade') {
      this.displayedMonth.set(new Date(cell.date));
      this.calendarView.set('year');
      return;
    }
    if (view === 'year') {
      this.displayedMonth.set(new Date(cell.date));
      this.calendarView.set('month');
      return;
    }

    const current = this.pendingValue() ?? this.currentValue() ?? new Date();
    const selected = new Date(current);
    selected.setFullYear(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate());
    this.pendingValue.set(this.clampValue(selected));
    this.displayedMonth.set(new Date(cell.date));
    this.activeTab.set('time');
  }

  selectToday(): void {
    const today = this.clampValue(new Date());
    if (isDateDisabled(today, this.disabledDates())) {
      return;
    }
    this.pendingValue.set(today);
    this.displayedMonth.set(today);
  }

  selectTimePart(part: 'hour' | 'minute' | 'second' | 'period', event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.updateTimePart(part, value);
  }

  handleTimeWheel(event: WheelEvent, part: 'hour' | 'minute' | 'second'): void {
    if (!this.enableMouseWheel() || event.deltaY === 0) {
      return;
    }
    event.preventDefault();
    const datePart = part as CueDateTimePart;
    const current = this.pendingValue() ?? this.currentValue() ?? new Date();
    const next = incrementDatePart(current, datePart, event.deltaY < 0 ? 1 : -1, this.steps());
    this.pendingValue.set(this.clampValue(next));
  }

  isTimeOptionDisabled(part: 'hour' | 'minute' | 'second', value: number): boolean {
    const pending = this.pendingValue() ?? new Date();
    const candidate = new Date(pending);
    if (part === 'hour') {
      const normalizedHour = this.usesTwelveHourClock()
        ? (value % 12) + (this.selectedPeriod() === 'pm' ? 12 : 0)
        : value;
      candidate.setHours(normalizedHour);
    } else if (part === 'minute') {
      candidate.setMinutes(value);
    } else {
      candidate.setSeconds(value);
    }
    const start = new Date(candidate);
    const end = new Date(candidate);
    if (part === 'hour') {
      start.setMinutes(0, 0, 0);
      end.setMinutes(59, 59, 999);
    } else if (part === 'minute') {
      start.setSeconds(0, 0);
      end.setSeconds(59, 999);
    } else {
      start.setMilliseconds(0);
      end.setMilliseconds(999);
    }
    const min = this.rangeBoundary(this.min(), candidate);
    const max = this.rangeBoundary(this.max(), candidate);
    return (!!min && end < min) || (!!max && start > max);
  }

  acceptPendingValue(): void {
    const value = this.pendingValue();
    if (value && (isDateDisabled(value, this.disabledDates()) || this.isOutsideRange(value))) {
      return;
    }
    this.commitValue(value);
    this.requestClose();
    this.inputElement()?.nativeElement.focus();
  }

  cancelPendingValue(): void {
    this.pendingValue.set(this.cloneOrNull(this.currentValue()));
    this.requestClose();
    this.inputElement()?.nativeElement.focus();
  }

  clearValue(event?: Event): void {
    event?.stopPropagation();
    if (this.isDisabled() || this.readonly()) {
      return;
    }
    this.inputElement()?.nativeElement.focus();
    this.commitValue(null);
  }

  moveCalendarFocus(event: KeyboardEvent): void {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const move = moves[event.key];
    if (!move) {
      return;
    }
    event.preventDefault();
    const buttons = Array.from(
      this.popupElement()?.nativeElement.querySelectorAll<HTMLButtonElement>('[data-calendar-cell]') ?? []
    ).filter((button) => !button.disabled);
    const currentIndex = buttons.indexOf(event.currentTarget as HTMLButtonElement);
    buttons[Math.max(0, Math.min(buttons.length - 1, currentIndex + move))]?.focus();
  }

  cellContext(cell: CueDateTimePickerCalendarCell): CueDateTimePickerCellContext {
    return {
      $implicit: cell.date,
      date: cell.date,
      disabled: cell.disabled,
      outsideCurrentPeriod: cell.outsideCurrentPeriod,
      selected: cell.selected,
      today: cell.today,
    };
  }

  weekNumberContext(row: CueDateTimePickerCalendarCell[]): CueDateTimePickerWeekNumberContext {
    const weekNumber = getIsoWeekNumber(row[0].date);
    return { $implicit: weekNumber, weekNumber, weekStart: row[0].date };
  }

  templateForCurrentView(): TemplateRef<CueDateTimePickerCellContext> | undefined {
    if (this.calendarView() === 'month') return this.monthCellTemplate() ?? this.cellTemplate();
    if (this.calendarView() === 'year') return this.yearCellTemplate();
    if (this.calendarView() === 'decade') return this.decadeCellTemplate();
    return this.centuryCellTemplate();
  }

  dateAriaLabel(date: Date): string {
    return new Intl.DateTimeFormat(this.locale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  formatTimePart(value: number): string {
    return String(value).padStart(2, '0');
  }

  writeValue(value: Date | null): void {
    this.applyExternalValue(isValidDate(value) ? value : null);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
    if (disabled) {
      this.openState.set(false);
    }
  }

  validate(control: AbstractControl<Date | null>): ValidationErrors | null {
    const value = control.value;
    const errors: ValidationErrors = {};
    const min = isValidDate(value) ? this.rangeBoundary(this.min(), value) : undefined;
    const max = isValidDate(value) ? this.rangeBoundary(this.max(), value) : undefined;

    if (this.rangeValidation() && isValidDate(value) && min && value < min) {
      errors['minError'] = { minValue: this.min(), value };
    }
    if (this.rangeValidation() && isValidDate(value) && max && value > max) {
      errors['maxError'] = { maxValue: this.max(), value };
    }
    if (this.disabledDatesValidation() && isValidDate(value) && isDateDisabled(value, this.disabledDates())) {
      errors['disabledDate'] = true;
    }
    if (this.incompleteDateValidation() && this.inputIncomplete()) {
      errors['incompleteDate'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private preparePopup(): void {
    const baseValue = this.currentValue() ?? this.focusedDate() ?? this.clampValue(this.inputBaseValue());
    this.pendingValue.set(new Date(baseValue));
    this.displayedMonth.set(new Date(baseValue));
    this.activeTab.set(this.timeOnly() ? 'time' : this.defaultTab());
    this.calendarView.set('month');
  }

  private applyExternalValue(value: Date | null): void {
    if (sameDate(this.currentValue(), value) && !this.inputIncomplete()) {
      return;
    }
    const clonedValue = this.cloneOrNull(value);
    this.currentValue.set(clonedValue);
    this.pendingValue.set(this.cloneOrNull(clonedValue));
    this.inputIncomplete.set(false);
    this.inputText.set(formatDateTime(clonedValue, this.format(), this.locale(), this.inputFocused()));
    if (clonedValue) {
      this.inputBaseValue.set(new Date(clonedValue));
      this.displayedMonth.set(new Date(clonedValue));
    }
  }

  private commitValue(value: Date | null, normalizeInputText = true): void {
    const clonedValue = this.cloneOrNull(value);
    const changed = !sameDate(this.currentValue(), clonedValue);
    this.currentValue.set(clonedValue);
    if (clonedValue) {
      this.inputBaseValue.set(new Date(clonedValue));
    }
    this.pendingValue.set(this.cloneOrNull(clonedValue));
    this.inputIncomplete.set(false);
    if (normalizeInputText) {
      this.inputText.set(formatDateTime(clonedValue, this.format(), this.locale(), this.inputFocused()));
    }
    if (changed) {
      this.valueChange.emit(this.cloneOrNull(clonedValue));
      this.onChange(this.cloneOrNull(clonedValue));
    }
    this.onValidatorChange();
  }

  private incrementInputValue(part: CueDateTimePart, amount: number): void {
    if (this.isDisabled() || this.readonly() || this.readOnlyInput()) {
      return;
    }
    const current = this.currentValue() ?? this.inputBaseValue();
    const next = incrementDatePart(current, part, amount, this.steps());
    if (this.timeOnly()) {
      next.setFullYear(current.getFullYear(), current.getMonth(), current.getDate());
    }
    this.commitValue(this.clampValue(next));
  }

  updateTimePart(part: 'hour' | 'minute' | 'second' | 'period', rawValue: string): void {
    const current = this.pendingValue() ?? this.currentValue() ?? new Date();
    const next = new Date(current);
    if (part === 'hour') {
      const hour = Number(rawValue);
      next.setHours(this.usesTwelveHourClock() ? (hour % 12) + (this.selectedPeriod() === 'pm' ? 12 : 0) : hour);
    } else if (part === 'minute') {
      next.setMinutes(Number(rawValue));
    } else if (part === 'second') {
      next.setSeconds(Number(rawValue));
    } else {
      next.setHours((next.getHours() % 12) + (rawValue === 'pm' ? 12 : 0));
    }
    this.pendingValue.set(this.clampValue(next));
  }

  private createPeriodCells(): CueDateTimePickerCalendarCell[] {
    const displayed = this.displayedMonth();
    const selected = this.pendingValue();
    const today = new Date();
    const view = this.calendarView();
    const cells: CueDateTimePickerCalendarCell[] = [];

    if (view === 'year') {
      const formatter = new Intl.DateTimeFormat(this.locale(), { month: 'short' });
      for (let month = 0; month < 12; month += 1) {
        const date = new Date(displayed.getFullYear(), month, 1);
        cells.push(this.createPeriodCell(date, formatter.format(date), selected, today, false));
      }
      return cells;
    }

    const interval = view === 'decade' ? 10 : 100;
    const step = view === 'decade' ? 1 : 10;
    const periodStart = Math.floor(displayed.getFullYear() / interval) * interval;
    for (let index = -1; index <= 10; index += 1) {
      const year = periodStart + index * step;
      const date = new Date(year, displayed.getMonth(), 1);
      const label = view === 'decade' ? String(year) : `${year}–${year + 9}`;
      cells.push(this.createPeriodCell(date, label, selected, today, index < 0 || index > 9));
    }
    return cells;
  }

  private createPeriodCell(
    date: Date,
    label: string,
    selected: Date | null,
    today: Date,
    outsideCurrentPeriod: boolean
  ): CueDateTimePickerCalendarCell {
    const view = this.calendarView();
    const start = new Date(date);
    const end = new Date(date);
    if (view === 'year') {
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (view === 'decade') {
      end.setFullYear(end.getFullYear() + 1, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else {
      end.setFullYear(end.getFullYear() + 10, 0, 0);
      end.setHours(23, 59, 59, 999);
    }
    const disabled = (isValidDate(this.min()) && end < this.min()!) || (isValidDate(this.max()) && start > this.max()!);
    const selectedInPeriod = this.dateFallsInCalendarPeriod(selected, date, view);
    const todayInPeriod = this.dateFallsInCalendarPeriod(today, date, view);
    return { date, disabled, label, outsideCurrentPeriod, selected: selectedInPeriod, today: todayInPeriod };
  }

  private dateFallsInCalendarPeriod(value: Date | null, cellDate: Date, view: CueDateTimePickerCalendarView): boolean {
    if (!isValidDate(value)) {
      return false;
    }
    if (view === 'year') {
      return value.getFullYear() === cellDate.getFullYear() && value.getMonth() === cellDate.getMonth();
    }
    if (view === 'decade') {
      return value.getFullYear() === cellDate.getFullYear();
    }
    return value.getFullYear() >= cellDate.getFullYear() && value.getFullYear() <= cellDate.getFullYear() + 9;
  }

  private getCalendarHeading(): string {
    const displayed = this.displayedMonth();
    const view = this.calendarView();
    if (view === 'month') {
      return new Intl.DateTimeFormat(this.locale(), { month: 'long', year: 'numeric' }).format(displayed);
    }
    if (view === 'year') {
      return String(displayed.getFullYear());
    }
    if (view === 'decade') {
      const start = Math.floor(displayed.getFullYear() / 10) * 10;
      return `${start}–${start + 9}`;
    }
    const start = Math.floor(displayed.getFullYear() / 100) * 100;
    return `${start}–${start + 99}`;
  }

  private isOutsideRange(value: Date): boolean {
    const min = this.rangeBoundary(this.min(), value);
    const max = this.rangeBoundary(this.max(), value);
    return (!!min && value < min) || (!!max && value > max);
  }

  private clampValue(value: Date): Date {
    return clampDate(value, this.rangeBoundary(this.min(), value), this.rangeBoundary(this.max(), value));
  }

  private rangeBoundary(boundary: Date | undefined, reference: Date): Date | undefined {
    if (!isValidDate(boundary)) {
      return undefined;
    }
    if (!this.timeOnly()) {
      return boundary;
    }
    const result = new Date(reference);
    result.setHours(boundary.getHours(), boundary.getMinutes(), boundary.getSeconds(), boundary.getMilliseconds());
    return result;
  }

  private normalizePopupClasses(value: CueDateTimePickerPopupSettings['popupClass']): string[] {
    const result = ['cue-datetimepicker-overlay-pane'];
    if (typeof value === 'string') {
      result.push(...value.split(/\s+/).filter(Boolean));
    } else if (Array.isArray(value)) {
      result.push(...value);
    } else if (value instanceof Set) {
      result.push(...value);
    } else if (value) {
      result.push(
        ...Object.entries(value)
          .filter(([, enabled]) => enabled)
          .map(([className]) => className)
      );
    }
    return result;
  }

  private cloneOrNull(value: Date | null): Date | null {
    return isValidDate(value) ? new Date(value) : null;
  }

  private includeSelectedOption(options: number[], selected: number): number[] {
    return options.includes(selected) ? options : [...options, selected].sort((left, right) => left - right);
  }

  private selectDatePartAtCaret(inputElement: HTMLInputElement): void {
    const format = resolveDateTimeFormat(this.format(), true);
    const caret = inputElement.selectionStart ?? 0;
    const tokenPattern = /yyyy|yyy|yy|y|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s|SSS|a/g;
    const tokens = Array.from(format.matchAll(tokenPattern));
    const token = tokens.find((item) => {
      const start = item.index ?? 0;
      return caret >= start && caret <= start + item[0].length;
    });
    if (token) {
      const start = token.index ?? 0;
      inputElement.setSelectionRange(start, start + token[0].length);
    }
  }

  private moveCaretToNextPart(inputElement: HTMLInputElement): void {
    const format = resolveDateTimeFormat(this.format(), true);
    const tokenPattern = /yyyy|yyy|yy|y|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s|SSS|a/g;
    const tokens = Array.from(format.matchAll(tokenPattern));
    const caret = inputElement.selectionStart ?? 0;
    const nextToken = tokens.find((token) => (token.index ?? 0) > caret);
    if (nextToken) {
      const start = nextToken.index ?? 0;
      inputElement.setSelectionRange(start, start + nextToken[0].length);
    }
  }

  private autoSwitchToNextPart(inputElement: HTMLInputElement): void {
    const format = resolveDateTimeFormat(this.format(), true);
    const tokenPattern = /yyyy|yyy|yy|y|MM|dd|HH|hh|mm|ss|SSS/g;
    const caret = inputElement.selectionStart ?? 0;
    const currentToken = Array.from(format.matchAll(tokenPattern)).find((token) => {
      const start = token.index ?? 0;
      return caret === start + token[0].length;
    });
    if (currentToken) {
      this.moveCaretToNextPart(inputElement);
    }
  }
}
